import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reportsApi, configApi, callLogsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, AlertCircle, CheckCircle, Link, BookOpen, Calendar, Phone, Download, BarChart3, Users, UserPlus, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '../components/ui/label';
import TermFinancialsReport from '../components/TermFinancialsReport';
import RegistrationsReport from '../components/RegistrationsReport';
import NewAdmissionReport from '../components/NewAdmissionReport';
import RegistrationInvoicedReport from '../components/RegistrationInvoicedReport';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  try {
    return new Date(timeStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const ReportsPage = () => {
  const navigate = useNavigate();
  // ?tab=<value> opens a tab directly (used by the Dashboard quick-access links).
  const [searchParams] = useSearchParams();
  const { user, isAdmin, userBranches } = useAuth();
  const canExport = user?.role === 'admin' || user?.role === 'manager';
  // Backend opens /reports/outstanding to admin + manager + accountant_assistant.
  const canViewOutstanding = ['admin', 'manager', 'accountant_assistant'].includes(user?.role);
  const [loading, setLoading] = useState(true);
  const [outstanding, setOutstanding] = useState([]);
  const [collectionSummary, setCollectionSummary] = useState({});
  const [discountAudit, setDiscountAudit] = useState([]);
  const [suspenseReport, setSuspenseReport] = useState({ transactions: [], total_amount: 0 });
  const [config, setConfig] = useState(null);
  const [exporting, setExporting] = useState(null);

  // Day Book state
  const [dayBookDate, setDayBookDate] = useState(new Date().toISOString().split('T')[0]);
  const [dayBookData, setDayBookData] = useState(null);
  const [dayBookLoading, setDayBookLoading] = useState(false);

  // Call Logs Report state
  const [callLogFromDate, setCallLogFromDate] = useState('');
  const [callLogToDate, setCallLogToDate] = useState('');
  const [callLogBranch, setCallLogBranch] = useState('all');
  const [callLogClass, setCallLogClass] = useState('all');
  const [callLogData, setCallLogData] = useState(null);
  const [callLogLoading, setCallLogLoading] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const [outstandingRes, collectionRes, discountRes, suspenseRes, configRes] = await Promise.all([
        reportsApi.outstanding(),
        reportsApi.collectionSummary(),
        reportsApi.discountAudit().catch(() => ({ data: [] })),
        reportsApi.suspense(),
        configApi.get()
      ]);
      setOutstanding(outstandingRes.data);
      setCollectionSummary(collectionRes.data);
      setDiscountAudit(discountRes.data);
      setSuspenseReport(suspenseRes.data);
      setConfig(configRes.data);
    } catch (err) {
      toast.error('Failed to load reports');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDayBook = useCallback(async () => {
    setDayBookLoading(true);
    try {
      const response = await reportsApi.dayBook({ date: dayBookDate });
      setDayBookData(response.data);
    } catch (err) {
      toast.error('Failed to load Day Book');
      console.error(err);
    } finally {
      setDayBookLoading(false);
    }
  }, [dayBookDate]);

  const fetchCallLogs = async () => {
    if (!callLogFromDate || !callLogToDate) { toast.error('Select date range'); return; }
    setCallLogLoading(true);
    try {
      const params = { from_date: callLogFromDate, to_date: callLogToDate, limit: 500 };
      if (callLogBranch !== 'all') params.branch = callLogBranch;
      if (callLogClass !== 'all') params.current_class = callLogClass;
      const res = await callLogsApi.getAll(params);
      setCallLogData(res.data);
    } catch (err) {
      toast.error('Failed to load call logs');
    } finally {
      setCallLogLoading(false);
    }
  };

  // Excel Export Functions
  const handleExportExcel = async (reportType) => {
    if (!canExport) {
      toast.error('Only Admin and Manager can export reports');
      return;
    }
    
    setExporting(reportType);
    try {
      let response;
      let filename;
      
      switch (reportType) {
        case 'outstanding':
          response = await reportsApi.exportOutstandingExcel();
          filename = `outstanding_report_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'collection':
          response = await reportsApi.exportCollectionExcel();
          filename = `collection_summary_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'daybook':
          response = await reportsApi.exportDayBookExcel({ date: dayBookDate });
          filename = `day_book_${dayBookDate}.xlsx`;
          break;
        case 'students':
          response = await reportsApi.exportStudentsExcel();
          filename = `students_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        default:
          throw new Error('Unknown report type');
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel exported successfully');
    } catch (err) {
      toast.error('Failed to export Excel');
      console.error(err);
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Prepare collection chart data
  const collectionChartData = Object.entries(collectionSummary).map(([date, data]) => ({
    date,
    cash: data.cash || 0,
    bank: data.bank_deposit || 0,
    total: data.total || 0
  })).slice(0, 14).reverse();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-[#5556A8]/10 text-[#5556A8]">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-[#C85A40]/10 text-[#C85A40]">Rejected</Badge>;
      default:
        return <Badge className="bg-[#D99C3B]/10 text-[#D99C3B]">Pending</Badge>;
    }
  };

  const getEntryTypeBadge = (type) => {
    switch (type) {
      case 'payment':
        return <Badge className="bg-[#5556A8]/10 text-[#5556A8]">Payment</Badge>;
      case 'bank_reconciliation':
        return <Badge className="bg-[#3E3F96]/10 text-[#3E3F96]">Bank Recon</Badge>;
      case 'expense':
        return <Badge className="bg-[#C85A40]/10 text-[#C85A40]">Expense</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Reports</h1>
          <p className="text-[#5C635F] mt-1">Financial reports and analytics</p>
        </div>
        
        <Button variant="outline" onClick={() => navigate('/tally-setup')} data-testid="tally-setup-btn">
          <Link className="w-4 h-4 mr-2" />
          Tally Integration
        </Button>
      </div>

      <Tabs defaultValue={searchParams.get('tab') || 'term-financials'} className="space-y-4">
        <TabsList className="bg-[#ECEDF5] flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="term-financials" data-testid="tab-term-financials">
            <BarChart3 className="w-3 h-3 mr-1" />Term Financials
          </TabsTrigger>
          <TabsTrigger value="registrations" data-testid="tab-registrations">
            <Users className="w-3 h-3 mr-1" />Registrations
          </TabsTrigger>
          <TabsTrigger value="new-admission" data-testid="tab-new-admission">
            <UserPlus className="w-3 h-3 mr-1" />New Admission
          </TabsTrigger>
          <TabsTrigger value="registration-invoiced" data-testid="tab-registration-invoiced">
            <FileCheck className="w-3 h-3 mr-1" />Registration Invoiced
          </TabsTrigger>
          {canViewOutstanding && <TabsTrigger value="outstanding" data-testid="tab-outstanding">Outstanding</TabsTrigger>}
          <TabsTrigger value="collection" data-testid="tab-collection">Collection</TabsTrigger>
          <TabsTrigger value="daybook" data-testid="tab-daybook" onClick={() => { if (!dayBookData) fetchDayBook(); }}>Day Book</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="tab-discounts">Discount Audit</TabsTrigger>
          <TabsTrigger value="suspense" data-testid="tab-suspense">Suspense</TabsTrigger>
          <TabsTrigger value="call-logs" data-testid="tab-call-logs-report">
            <Phone className="w-3 h-3 mr-1" />Call Logs
          </TabsTrigger>
        </TabsList>

        {/* Term-wise Financials (Branch x Term x FY) */}
        <TabsContent value="term-financials">
          <TermFinancialsReport configBranches={config?.branches || []}
            configClasses={config?.classes || []} />
        </TabsContent>

        {/* Fee-head wise registrations (FY x Term x Fee Head), every billable head */}
        <TabsContent value="registrations">
          <RegistrationsReport configBranches={config?.branches || []}
            configClasses={config?.classes || []}
            configFeeHeads={config?.fee_head_display || {}} />
        </TabsContent>

        {/* New Admission Report (Branch x Class head-count from a start date) */}
        <TabsContent value="new-admission">
          <NewAdmissionReport configBranches={config?.branches || []}
            configClasses={config?.classes || []} />
        </TabsContent>

        {/* Registration fee: admission cohort vs registration invoice lines */}
        <TabsContent value="registration-invoiced">
          <RegistrationInvoicedReport configBranches={config?.branches || []}
            configClasses={config?.classes || []} />
        </TabsContent>

        {/* Outstanding Report - Admin Only */}
        {canViewOutstanding && (
        <TabsContent value="outstanding">
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#C85A40]" />
                Outstanding Fees (Overdue Invoices)
              </CardTitle>
              {canExport && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleExportExcel('outstanding')}
                  disabled={exporting === 'outstanding'}
                  data-testid="export-outstanding-btn"
                >
                  {exporting === 'outstanding' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export Excel
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {outstanding.length === 0 ? (
                <div className="p-8 text-center text-[#5C635F]">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-[#5556A8]" />
                  <p>No outstanding fees!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5FA]">
                        <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Class</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Branch</TableHead>
                        <TableHead className="text-xs uppercase font-semibold text-right">Invoice Outstanding</TableHead>
                        <TableHead className="text-xs uppercase font-semibold text-right">Late Fee</TableHead>
                        <TableHead className="text-xs uppercase font-semibold text-right">Effective Outstanding</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Pending Invoices</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {outstanding.slice(0, 50).map((item, idx) => {
                        const effective = item.effective_total_outstanding ?? item.total_outstanding;
                        const surcharge = item.surcharge_total ?? 0;
                        return (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.student_name}</TableCell>
                            <TableCell>{item.class}</TableCell>
                            <TableCell>{item.branch}</TableCell>
                            <TableCell className="text-right tabular-nums text-[#5C635F]">
                              {formatCurrency(item.total_outstanding)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-[#D99C3B]">
                              {surcharge > 0 ? formatCurrency(surcharge) : '—'}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-bold text-[#C85A40]">
                              {formatCurrency(effective)}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {item.invoices?.slice(0, 3).map((inv, i) => (
                                  <Badge key={i} variant="outline" className="text-xs capitalize">
                                    {inv.fee_head}
                                  </Badge>
                                ))}
                                {item.invoices?.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{item.invoices.length - 3}</Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Collection Summary */}
        <TabsContent value="collection">
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Daily Collection Summary</CardTitle>
              {canExport && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleExportExcel('collection')}
                  disabled={exporting === 'collection'}
                  data-testid="export-collection-btn"
                >
                  {exporting === 'collection' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export Excel
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {collectionChartData.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={collectionChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#D8D9E8" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #D8D9E8' }}
                      />
                      <Bar dataKey="cash" name="Cash" fill="#3E3F96" stackId="a" />
                      <Bar dataKey="bank" name="Bank" fill="#5556A8" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-[#5C635F]">
                  No collection data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Day Book */}
        <TabsContent value="daybook">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#3E3F96]" />
                  Day Book
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#5C635F]" />
                  <Input
                    type="date"
                    value={dayBookDate}
                    onChange={(e) => setDayBookDate(e.target.value)}
                    className="w-44"
                    data-testid="daybook-date-input"
                  />
                  <Button
                    onClick={fetchDayBook}
                    size="sm"
                    className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                    disabled={dayBookLoading}
                    data-testid="daybook-fetch-btn"
                  >
                    {dayBookLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load'}
                  </Button>
                  {canExport && dayBookData && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleExportExcel('daybook')}
                      disabled={exporting === 'daybook'}
                      data-testid="export-daybook-btn"
                    >
                      {exporting === 'daybook' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {dayBookLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-6 h-6 animate-spin text-[#3E3F96]" />
                </div>
              ) : !dayBookData ? (
                <div className="flex flex-col items-center justify-center h-48 text-[#5C635F]">
                  <BookOpen className="w-12 h-12 mb-4 opacity-50" />
                  <p>Select a date and click Load to view the Day Book</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 bg-[#5556A8]/10 rounded-lg">
                      <p className="text-xs text-[#5C635F] uppercase font-semibold">Total Credit (Received)</p>
                      <p className="text-xl font-bold text-[#5556A8] tabular-nums" data-testid="daybook-total-credit">
                        {formatCurrency(dayBookData.summary?.total_credit)}
                      </p>
                    </div>
                    <div className="p-4 bg-[#C85A40]/10 rounded-lg">
                      <p className="text-xs text-[#5C635F] uppercase font-semibold">Total Debit (Spent)</p>
                      <p className="text-xl font-bold text-[#C85A40] tabular-nums" data-testid="daybook-total-debit">
                        {formatCurrency(dayBookData.summary?.total_debit)}
                      </p>
                    </div>
                    <div className="p-4 bg-[#3E3F96]/10 rounded-lg">
                      <p className="text-xs text-[#5C635F] uppercase font-semibold">Net Balance</p>
                      <p className={`text-xl font-bold tabular-nums ${dayBookData.summary?.net_balance >= 0 ? 'text-[#5556A8]' : 'text-[#C85A40]'}`} data-testid="daybook-net-balance">
                        {formatCurrency(dayBookData.summary?.net_balance)}
                      </p>
                    </div>
                  </div>

                  {/* Counts */}
                  <div className="flex gap-3 flex-wrap">
                    <Badge variant="outline" className="text-xs">Payments: {dayBookData.counts?.payments || 0}</Badge>
                    <Badge variant="outline" className="text-xs">Bank Recon: {dayBookData.counts?.bank_reconciliation || 0}</Badge>
                    <Badge variant="outline" className="text-xs">Expenses: {dayBookData.counts?.expenses || 0}</Badge>
                  </div>

                  {/* Entries Table */}
                  {dayBookData.entries?.length === 0 ? (
                    <div className="p-8 text-center text-[#5C635F]">
                      No transactions found for {formatDate(dayBookData.date)}
                    </div>
                  ) : (
                    <div className="overflow-x-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-[#F5F5FA]">
                            <TableHead className="text-xs uppercase font-semibold">Time</TableHead>
                            <TableHead className="text-xs uppercase font-semibold">Type</TableHead>
                            <TableHead className="text-xs uppercase font-semibold">Reference</TableHead>
                            <TableHead className="text-xs uppercase font-semibold">Description</TableHead>
                            <TableHead className="text-xs uppercase font-semibold text-right">Debit (DR)</TableHead>
                            <TableHead className="text-xs uppercase font-semibold text-right">Credit (CR)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dayBookData.entries.map((entry, idx) => (
                            <TableRow key={idx} data-testid={`daybook-entry-${idx}`}>
                              <TableCell className="text-[#5C635F] text-sm">{formatTime(entry.time)}</TableCell>
                              <TableCell>{getEntryTypeBadge(entry.type)}</TableCell>
                              <TableCell className="font-medium">{entry.reference || '-'}</TableCell>
                              <TableCell className="text-sm max-w-xs truncate">{entry.description}</TableCell>
                              <TableCell className="text-right tabular-nums font-medium text-[#C85A40]">
                                {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                              </TableCell>
                              <TableCell className="text-right tabular-nums font-medium text-[#5556A8]">
                                {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Totals Row */}
                          <TableRow className="bg-[#F5F5FA] font-bold">
                            <TableCell colSpan={4} className="text-right font-semibold">TOTALS</TableCell>
                            <TableCell className="text-right tabular-nums text-[#C85A40]">
                              {formatCurrency(dayBookData.summary?.total_debit)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-[#5556A8]">
                              {formatCurrency(dayBookData.summary?.total_credit)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discount Audit */}
        <TabsContent value="discounts">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg">Discount Audit Trail</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {discountAudit.length === 0 ? (
                <div className="p-8 text-center text-[#5C635F]">No discount records</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5FA]">
                        <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Type</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Percentage</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Requested By</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Approved By</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {discountAudit.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.student_name}</TableCell>
                          <TableCell className="capitalize">{item.discount_type?.replace(/_/g, ' ')}</TableCell>
                          <TableCell className="tabular-nums font-medium text-[#3E3F96]">{item.percentage}%</TableCell>
                          <TableCell>{item.requester_name || '-'}</TableCell>
                          <TableCell>{item.approver_name || '-'}</TableCell>
                          <TableCell>{getStatusBadge(item.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suspense Report */}
        <TabsContent value="suspense">
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Suspense Account</CardTitle>
              <div className="text-right">
                <p className="text-sm text-[#5C635F]">Total Suspense</p>
                <p className="text-xl font-bold text-[#C85A40] tabular-nums">
                  {formatCurrency(suspenseReport.total_amount)}
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {suspenseReport.transactions.length === 0 ? (
                <div className="p-8 text-center text-[#5C635F]">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-[#5556A8]" />
                  <p>No transactions in suspense!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5FA]">
                        <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Reference</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Bank</TableHead>
                        <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suspenseReport.transactions.map((txn) => (
                        <TableRow key={txn.id}>
                          <TableCell className="text-[#5C635F]">{formatDate(txn.date)}</TableCell>
                          <TableCell className="font-medium">{txn.reference_number}</TableCell>
                          <TableCell>{txn.bank_name}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium text-[#C85A40]">
                            {formatCurrency(txn.amount, txn.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Call Logs Report */}
        <TabsContent value="call-logs" className="space-y-4">
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="space-y-1">
                  <Label className="text-xs text-[#5C635F]">From Date</Label>
                  <Input type="date" value={callLogFromDate} onChange={e => setCallLogFromDate(e.target.value)} data-testid="call-report-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#5C635F]">To Date</Label>
                  <Input type="date" value={callLogToDate} onChange={e => setCallLogToDate(e.target.value)} data-testid="call-report-to" />
                </div>
                <Select value={callLogBranch} onValueChange={setCallLogBranch}>
                  <SelectTrigger className="w-40" data-testid="call-report-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {(isAdmin ? config?.branches : userBranches)?.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={callLogClass} onValueChange={setCallLogClass}>
                  <SelectTrigger className="w-40" data-testid="call-report-class"><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {config?.classes?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={fetchCallLogs} disabled={callLogLoading} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="fetch-call-logs-btn">
                  {callLogLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
                  Generate
                </Button>
              </div>
            </CardContent>
          </Card>

          {callLogData && (
            <Card className="border-[#D8D9E8]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#3E3F96]" />
                  Call Logs ({callLogData.total} records)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5FA]">
                        <TableHead className="text-xs uppercase font-semibold">Call Date</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Student ID</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Student Name</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Branch</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Class</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Feedback</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Commitment Date</TableHead>
                        <TableHead className="text-xs uppercase font-semibold">Logged By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {callLogData.logs?.map(log => (
                        <TableRow key={log.id} data-testid={`call-report-row-${log.id}`}>
                          <TableCell className="text-[#5C635F]">{formatDate(log.call_date)}</TableCell>
                          <TableCell className="font-medium text-[#3E3F96]">{log.student_code}</TableCell>
                          <TableCell>{log.student_name}</TableCell>
                          <TableCell className="text-[#5C635F]">{log.branch}</TableCell>
                          <TableCell><Badge variant="secondary" className="bg-[#ECEDF5] text-[#3E3F96]">{log.current_class}</Badge></TableCell>
                          <TableCell className="max-w-xs truncate">{log.feedback}</TableCell>
                          <TableCell className="font-medium text-[#3E3F96]">{log.expected_payment_date ? formatDate(log.expected_payment_date) : '-'}</TableCell>
                          <TableCell className="text-sm text-[#5C635F]">{log.logged_by_name}</TableCell>
                        </TableRow>
                      ))}
                      {(!callLogData.logs || callLogData.logs.length === 0) && (
                        <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#5C635F]">No call logs found for this period</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
